import type React from 'react';
import { useMemo } from 'react';
import type { Paths } from 'type-fest';

import type { Features } from '@/config/types.config';
import { hasProperty, isObject } from '@repo/utils/type.utils';

import { getAppConfig } from '@/utils/window.utils';

interface Props {
    children: React.ReactNode;
    configPath?: FeaturePaths;
    when?: () => boolean;
}

type FeaturePaths = Array<Paths<Features, { leavesOnly: true }>>;

const getNestedValues = (obj: Features, paths: FeaturePaths): unknown[] => {
    return paths.map((path) => {
        const pathSegments = path.split('.');
        let currentValue: unknown = obj;

        for (const segment of pathSegments) {
            if (isObject(currentValue) && hasProperty(currentValue, segment)) {
                currentValue = currentValue[segment];
            } else {
                return;
            }
        }

        return currentValue;
    });
};

const evaluateFeatureValue = (value: unknown): boolean => {
    if (isObject(value) && hasProperty(value, 'enable')) {
        return Boolean(value.enable);
    }
    return Boolean(value);
};

export const Feature = ({ children, configPath, when }: Props): React.ReactNode => {
    const config = getAppConfig();

    const configEnabled = useMemo(() => {
        if (!configPath?.length) {
            return true;
        }

        const values = getNestedValues(config.feature, configPath);
        return values.every(evaluateFeatureValue);
    }, [config.feature, configPath]);

    const functionEnabled = useMemo(() => when?.() ?? true, [when]);

    return configEnabled && functionEnabled ? children : null;
};
